import { Tokenizer } from './tokenizer.ts';
import { damerauLevenshteinDistance } from '../../../cross-app-shared/services/damerau-levenshtein-distance.ts';
import {
  Acre,
  Btu,
  CalendarDate,
  CalendarHalfYear,
  CalendarMonth,
  CalendarQuarter,
  CalendarWeek,
  CalendarYear,
  Centimeter,
  type CompoundUnit,
  CubicFoot,
  Day,
  Decimeter,
  FluidOunce,
  Foot,
  Gallon,
  Grain,
  Gram,
  Hour,
  Hundredweight,
  Inch,
  Joule,
  Kilogram,
  Kilometer,
  Liter,
  Meter,
  MetricTon,
  Mile,
  Milligram,
  Minute,
  MMBtu,
  MMSCF,
  Month,
  Ounce,
  PartsPerBillion,
  PartsPerMillion,
  Percent,
  Pound,
  Second,
  SquareFoot,
  SquareKilometer,
  SquareMile,
  Therm,
  Ton,
  type Unit,
  UnitsTracker,
  Week,
  Yard,
  Year,
} from './units.ts';

// Our formulas persist units as strings, so the exact spelling matters, and the set of spellings we understand is not
// discoverable from a bare text box. This catalog describes every unit in human terms so that a picker can search it,
// and so that typed units can be checked (and typos corrected) before they cause a conversion error in a report.

export type UnitCategory = 'Mass' | 'Volume' | 'Length' | 'Area' | 'Time' | 'Energy' | 'Concentration' | 'Calendar';

export interface UnitCatalogEntry {
  unit: Unit | CompoundUnit;
  /** The spelling the picker writes. Always one of `unit.names`. */
  spelling: string;
  /** Singular, lowercase, used to describe compound units ("pound per hour"). */
  label: string;
  category: UnitCategory;
  /** Extra search terms that are not spellings of the unit. */
  keywords: readonly string[];
  /** Anything surprising about how this unit converts. */
  note: string | null;
  /** Common units are offered first, and are used to build suggested compound units. */
  common: boolean;
}

function entry(
  unit: Unit | CompoundUnit,
  spelling: string,
  label: string,
  category: UnitCategory,
  { keywords = [], note = null, common = false }: { keywords?: string[]; note?: string | null; common?: boolean },
): UnitCatalogEntry {
  return { unit, spelling, label, category, keywords, note, common };
}

export const unitCatalog: readonly UnitCatalogEntry[] = [
  entry(Pound, 'lbs', 'pound', 'Mass', { keywords: ['weight'], common: true }),
  entry(Ton, 'tons', 'short ton', 'Mass', { keywords: ['weight', 'us ton'], note: '2,000 lbs', common: true }),
  entry(Kilogram, 'kg', 'kilogram', 'Mass', { keywords: ['weight'], common: true }),
  entry(MetricTon, 'tonnes', 'metric ton', 'Mass', { keywords: ['weight'], note: '1,000 kg', common: true }),
  entry(Gram, 'g', 'gram', 'Mass', { keywords: ['weight'], common: true }),
  entry(Milligram, 'mg', 'milligram', 'Mass', { keywords: ['weight'] }),
  entry(Ounce, 'oz', 'ounce', 'Mass', { keywords: ['weight'], note: 'By weight. Use floz for volume.' }),
  entry(Grain, 'grains', 'grain', 'Mass', { keywords: ['weight', 'gr'], note: '1/7,000 lb' }),
  entry(Hundredweight, 'cwt', 'hundredweight', 'Mass', { keywords: ['weight'], note: '100 lbs' }),

  entry(Gallon, 'gal', 'gallon', 'Volume', { note: 'US gallon', common: true }),
  entry(Liter, 'L', 'liter', 'Volume', { keywords: ['litre'], common: true }),
  entry(CubicFoot, 'cuft', 'cubic foot', 'Volume', { keywords: ['ft3', 'cf'], common: true }),
  entry(FluidOunce, 'floz', 'fluid ounce', 'Volume', { note: 'US fluid ounce' }),

  entry(Foot, 'ft', 'foot', 'Length', { keywords: ['distance'], common: true }),
  entry(Inch, 'in', 'inch', 'Length', { keywords: ['distance'] }),
  entry(Yard, 'yd', 'yard', 'Length', { keywords: ['distance'] }),
  entry(Mile, 'mi', 'mile', 'Length', { keywords: ['distance'], common: true }),
  entry(Meter, 'm', 'meter', 'Length', { keywords: ['distance', 'metre'], common: true }),
  entry(Kilometer, 'km', 'kilometer', 'Length', { keywords: ['distance', 'kilometre'] }),
  entry(Centimeter, 'cm', 'centimeter', 'Length', { keywords: ['distance', 'centimetre'] }),
  entry(Decimeter, 'dm', 'decimeter', 'Length', { keywords: ['distance', 'decimetre'] }),

  entry(SquareFoot, 'sqft', 'square foot', 'Area', { keywords: ['ft2'], common: true }),
  entry(Acre, 'acres', 'acre', 'Area', { common: true }),
  entry(SquareMile, 'sqmi', 'square mile', 'Area', {}),
  entry(SquareKilometer, 'sqkm', 'square kilometer', 'Area', {}),

  entry(Hour, 'hr', 'hour', 'Time', { keywords: ['duration'], common: true }),
  entry(Day, 'day', 'day', 'Time', { keywords: ['duration', 'daily'], common: true }),
  entry(Year, 'yr', 'year', 'Time', { keywords: ['duration', 'annual'], note: '365 days', common: true }),
  entry(Month, 'mo', 'month', 'Time', { keywords: ['duration', 'monthly'], note: '30 days', common: true }),
  entry(Week, 'wk', 'week', 'Time', { keywords: ['duration', 'weekly'] }),
  entry(Minute, 'min', 'minute', 'Time', { keywords: ['duration'] }),
  entry(Second, 's', 'second', 'Time', { keywords: ['duration'] }),

  entry(MMBtu, 'MMBtu', 'MMBtu', 'Energy', { keywords: ['million btu', 'heat input'], common: true }),
  entry(MMSCF, 'MMscf', 'MMscf', 'Energy', {
    keywords: ['natural gas', 'million standard cubic feet'],
    note: 'Natural gas, treated as energy at 1,020 Btu/scf',
    common: true,
  }),
  entry(Therm, 'therms', 'therm', 'Energy', { keywords: ['natural gas'], note: '100,000 Btu', common: true }),
  entry(Btu, 'Btu', 'Btu', 'Energy', { keywords: ['british thermal unit', 'heat'] }),
  entry(Joule, 'J', 'joule', 'Energy', {}),

  entry(Percent, '%', 'percent', 'Concentration', { keywords: ['ratio', 'fraction'], common: true }),
  entry(PartsPerMillion, 'ppm', 'part per million', 'Concentration', { common: true }),
  entry(PartsPerBillion, 'ppb', 'part per billion', 'Concentration', {}),

  // Formulas use these to group dates into reporting periods. They are not quantities, so the picker never offers them,
  // but they are still recognized as valid units:
  entry(CalendarDate, 'caldate', 'calendar date', 'Calendar', {}),
  entry(CalendarWeek, 'calweek', 'calendar week', 'Calendar', {}),
  entry(CalendarMonth, 'calmonth', 'calendar month', 'Calendar', {}),
  entry(CalendarQuarter, 'calquarter', 'calendar quarter', 'Calendar', {}),
  entry(CalendarHalfYear, 'calhalfyear', 'calendar half year', 'Calendar', {}),
  entry(CalendarYear, 'calyear', 'calendar year', 'Calendar', {}),
];

const catalogByName = new Map<string, UnitCatalogEntry>(
  unitCatalog.flatMap((e) => e.unit.names.map((name): [string, UnitCatalogEntry] => [name, e])),
);

/** Compound units commonly found on permits and emission factors, offered before the user has typed anything. */
export const unitCommonCompounds: readonly string[] = [
  'lbs/hr',
  'tons/yr',
  'lbs/day',
  'lbs/yr',
  'tons/mo',
  'lbs/MMBtu',
  'lbs/MMscf',
  'lbs/gal',
  'lbs/tons',
  'kg/hr',
  'tonnes/yr',
  'gal/hr',
  'gal/yr',
  'MMBtu/hr',
  'MMscf/yr',
];

/** Shorthand people commonly type that is not a unit we understand, mapped to what they meant. */
const unitShorthands: ReadonlyMap<string, string> = new Map([
  ['tpy', 'tons/yr'],
  ['tpd', 'tons/day'],
  ['tph', 'tons/hr'],
  ['pph', 'lbs/hr'],
  ['lbhr', 'lbs/hr'],
  ['lbshr', 'lbs/hr'],
  ['gpm', 'gal/min'],
  ['gph', 'gal/hr'],
]);

/**
 * What a picker may accept:
 * - `any`: any unit we understand, plus (when `allowCustom`) names we don't, such as `cars/hr` in an intermediate
 *   formula, which are fine until something needs to convert them.
 * - `convertibleTo`: anything that converts to `unit`. "Must be a mass" is `{ unit: 'lbs' }`, "must be a density" is
 *   `{ unit: 'lbs/gal' }`. `label` overrides the generated description of what is required (e.g. "density").
 * - `oneOf`: exactly one of a fixed list of spellings.
 */
export type UnitRestriction =
  | { kind: 'any'; allowCustom: boolean }
  | { kind: 'convertibleTo'; unit: string; label: string | null }
  | { kind: 'oneOf'; units: readonly string[] };

interface UnitPart {
  name: string;
  exponent: number;
}

type UnitParse = { parts: UnitPart[] } | { error: string };

const unitPartPattern = '[a-zA-Z%]+(?:\\^-?\\d+)?';
const unitSidePattern = `${unitPartPattern}(?:-${unitPartPattern})*`;
const unitStringPattern = new RegExp(`^${unitSidePattern}(?:/${unitSidePattern})?$`);

/**
 * Stricter than the formula tokenizer, which stops at the first character it doesn't expect and silently ignores the
 * rest (so `lbs per hr` would be read as `lbs`).
 */
function unitStringParse(value: string): UnitParse {
  if (/\s/.test(value)) {
    return { error: 'Units cannot contain spaces.' };
  } else if ((value.match(/\//g) ?? []).length > 1) {
    return { error: 'Units can only have one "/". Join units in the denominator with "-", like lbs/MMBtu-hr.' };
  } else if (!unitStringPattern.test(value)) {
    return { error: 'Units are made of names joined by "/" (divide) or "-" (multiply), like lbs/hr or ft-lbs.' };
  }

  const tokenized = Tokenizer.tokenizeUnits(value);
  if ('error' in tokenized) {
    return { error: tokenized.error };
  }

  return { parts: tokenized.units.map(({ unit, exponent }) => ({ name: unit, exponent })) };
}

function unitPartsToString(parts: UnitPart[]): string {
  const side = (sideParts: UnitPart[]) =>
    sideParts
      .map(({ name, exponent }) => (Math.abs(exponent) === 1 ? name : `${name}^${Math.abs(exponent)}`))
      .join('-');
  const numerator = parts.filter((p) => p.exponent > 0);
  const denominator = parts.filter((p) => p.exponent < 0);
  if (numerator.length === 0) {
    return parts.map(({ name, exponent }) => (exponent === 1 ? name : `${name}^${exponent}`)).join('-');
  }
  return denominator.length > 0 ? `${side(numerator)}/${side(denominator)}` : side(numerator);
}

/** Describes a unit string in words, e.g. `lbs/MMBtu` is "pound per MMBtu". Unknown names are described as written. */
export function unitStringDescribe(value: string): string {
  const parsed = unitStringParse(value.trim());
  if ('error' in parsed) {
    return value;
  }

  const describe = ({ name, exponent }: UnitPart) => {
    const label = catalogByName.get(name)?.label ?? name;
    const power = Math.abs(exponent);
    return power === 1 ? label : power === 2 ? `square ${label}` : power === 3 ? `cubic ${label}` : `${label}^${power}`;
  };
  const numerator = parsed.parts.filter((p) => p.exponent > 0).map(describe);
  const denominator = parsed.parts.filter((p) => p.exponent < 0).map(describe);
  if (numerator.length === 0) {
    return `per ${denominator.join('-')}`;
  }
  return denominator.length > 0 ? `${numerator.join('-')} per ${denominator.join('-')}` : numerator.join('-');
}

function dimensionWord(name: string, exponent: number): string {
  const power = Math.abs(exponent);
  if (name === 'Length') {
    return power === 1 ? 'length' : power === 2 ? 'area' : power === 3 ? 'volume' : `length^${power}`;
  }
  const word =
    name === 'Duration'
      ? 'time'
      : name === 'Dimensionless'
        ? 'concentration'
        : name === 'Calendar'
          ? 'calendar period'
          : name.toLowerCase();
  return power === 1 ? word : `${word}^${power}`;
}

/** Describes what kind of quantity a unit string measures, e.g. `lbs/hr` is "mass per time". */
export function unitDimensionDescribe(tracker: UnitsTracker): string {
  const numerator: string[] = [];
  const denominator: string[] = [];
  for (const [name, exponent] of tracker.dimensionExponents()) {
    (exponent > 0 ? numerator : denominator).push(dimensionWord(name, exponent));
  }

  if (numerator.length === 0 && denominator.length === 0) {
    return 'unitless';
  } else if (numerator.length === 0) {
    return `per ${denominator.join(' × ')}`;
  }
  return denominator.length > 0 ? `${numerator.join(' × ')} per ${denominator.join(' × ')}` : numerator.join(' × ');
}

/** The closest known spelling to an unknown unit name, or null if nothing is close enough to be a likely typo. */
function unitNameCorrect(name: string): string | null {
  const lower = name.toLowerCase();

  // Wrong case, e.g. LBS or Kg. Prefer the spelling with the same characters over the catalog spelling:
  const caseInsensitive = unitCatalog.flatMap((e) => e.unit.names).find((n) => n.toLowerCase() === lower);
  if (caseInsensitive) {
    return caseInsensitive;
  }

  const shorthand = unitShorthands.get(lower);
  if (shorthand) {
    return shorthand;
  }

  if (lower.length < 2) {
    return null;
  }
  const allowed = lower.length <= 4 ? 1 : 2;
  let best: { entry: UnitCatalogEntry; distance: number } | null = null;
  for (const e of unitCatalog) {
    if (e.category === 'Calendar') continue;
    for (const n of e.unit.names) {
      if (n.length < 2) continue;
      const distance = damerauLevenshteinDistance(lower, n.toLowerCase()).distance;
      if (
        distance <= allowed &&
        (!best || distance < best.distance || (distance === best.distance && e.common && !best.entry.common))
      ) {
        best = { entry: e, distance };
      }
    }
  }

  return best?.entry.spelling ?? null;
}

export type UnitAnalysis =
  | { status: 'empty' }
  | { status: 'ok'; description: string; dimension: string }
  /** Allowed, but contains names we don't understand, so it cannot be converted for a report. */
  | { status: 'custom'; description: string; message: string; suggestion: string | null }
  | { status: 'invalid'; message: string; suggestion: string | null };

/** What is required by a restriction, in words, e.g. "mass per time (like lbs/hr)". */
export function unitRestrictionDescribe(restriction: UnitRestriction): string | null {
  if (restriction.kind === 'any') {
    return null;
  } else if (restriction.kind === 'convertibleTo') {
    const label = restriction.label ?? unitDimensionDescribe(UnitsTracker.fromParsedUnitString(restriction.unit));
    return `${label} (like ${restriction.unit})`;
  } else if (restriction.kind === 'oneOf') {
    return `one of ${restriction.units.join(', ')}`;
  } else {
    const unexpected: never = restriction;
    throw new Error(`Unexpected restriction: ${JSON.stringify(unexpected)}`);
  }
}

/** Checks a typed unit string against a restriction, explaining any problem and offering a correction if we have one. */
export function unitStringAnalyze(value: string, restriction: UnitRestriction): UnitAnalysis {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { status: 'empty' };
  }

  const parsed = unitStringParse(trimmed);
  if ('error' in parsed) {
    const despaced = trimmed.replace(/\s*\bper\b\s*/gi, '/').replace(/\s+/g, '');
    const suggestion = despaced !== trimmed && 'parts' in unitStringParse(despaced) ? despaced : null;
    const corrected = suggestion ? unitStringAnalyze(suggestion, restriction) : null;
    return {
      status: 'invalid',
      message: parsed.error,
      suggestion:
        corrected && corrected.status !== 'ok' && 'suggestion' in corrected ? corrected.suggestion : suggestion,
    };
  }

  const unknownNames = parsed.parts.map((p) => p.name).filter((name) => !catalogByName.has(name));
  if (unknownNames.length > 0) {
    const corrected = parsed.parts.map((part) => {
      if (catalogByName.has(part.name)) return part;
      return { name: unitNameCorrect(part.name) ?? part.name, exponent: part.exponent };
    });
    const correctedString = unitPartsToString(corrected);
    const suggestionAnalysis = correctedString !== trimmed ? unitStringAnalyze(correctedString, restriction) : null;
    const suggestion = suggestionAnalysis && suggestionAnalysis.status !== 'invalid' ? correctedString : null;
    const names = unknownNames.map((n) => `"${n}"`).join(' and ');
    const plural = unknownNames.length > 1;

    if (restriction.kind === 'any' && restriction.allowCustom) {
      return {
        status: 'custom',
        description: unitStringDescribe(trimmed),
        message:
          `${names} ${plural ? "aren't units" : "isn't a unit"} we know. That's fine as long as ` +
          `${plural ? 'they cancel' : 'it cancels'} out before a report needs to convert it.`,
        suggestion,
      };
    }
    return { status: 'invalid', message: `${names} ${plural ? "aren't units" : "isn't a unit"} we know.`, suggestion };
  }

  const tracker = UnitsTracker.fromParsedUnitString(trimmed);
  const ok: UnitAnalysis = {
    status: 'ok',
    description: unitStringDescribe(trimmed),
    dimension: unitDimensionDescribe(tracker),
  };

  if (restriction.kind === 'any') {
    return ok;
  } else if (restriction.kind === 'convertibleTo') {
    if (UnitsTracker.canConvert({ from: trimmed, to: restriction.unit })) {
      return ok;
    }
    return {
      status: 'invalid',
      message: `Expected ${unitRestrictionDescribe(restriction)}, but ${trimmed} is ${ok.dimension}.`,
      suggestion: null,
    };
  } else if (restriction.kind === 'oneOf') {
    if (restriction.units.includes(trimmed)) {
      return ok;
    }
    const equivalent = restriction.units.find((u) => UnitsTracker.fromParsedUnitString(u).equals(tracker)) ?? null;
    return { status: 'invalid', message: `Expected ${unitRestrictionDescribe(restriction)}.`, suggestion: equivalent };
  } else {
    const unexpected: never = restriction;
    throw new Error(`Unexpected restriction: ${JSON.stringify(unexpected)}`);
  }
}

export interface UnitOption {
  spelling: string;
  description: string;
  /** e.g. "mass per time" */
  dimension: string;
  note: string | null;
  /** When the search matched a different spelling of the unit than the one we write, e.g. "pounds" for lbs. */
  matchedAlias: string | null;
  /**
   * Only for `convertibleTo`: a unit that is on the way to being acceptable but needs more, e.g. `lbs` when `lbs/hr` is
   * required. Choosing it should append `continueWith` and keep the picker open.
   */
  continueWith: '/' | '-' | null;
  /**
   * Whether this plainly is what was typed (a spelling, shorthand, or name), rather than a loose or fuzzy match. Only a
   * confident match should be chosen by pressing Enter, so a deliberate custom unit is never replaced by a guess.
   */
  confident: boolean;
}

export type UnitOptionGroup = { heading: string; options: UnitOption[] };

/**
 * Splits off the unit name being typed at the end of the input, e.g. `lbs/h` is `{ head: 'lbs/', segment: 'h' }`, so
 * that we can complete one piece of a compound unit at a time.
 */
function unitQuerySplit(query: string): { head: string; segment: string } {
  const segment = /[a-zA-Z%]*$/.exec(query)![0];
  return { head: query.slice(0, query.length - segment.length), segment };
}

/** How well a search term matches a catalog entry; 0 for no match. */
function unitEntryScore(
  e: UnitCatalogEntry,
  term: string,
): { score: number; matchedAlias: string | null; confident: boolean } {
  if (term === '') {
    return { score: e.common ? 2 : 1, matchedAlias: null, confident: false };
  }

  const lower = term.toLowerCase();
  let best = { score: 0, matchedAlias: null as string | null };
  const consider = (score: number, matchedAlias: string | null) => {
    if (score > best.score) best = { score, matchedAlias };
  };

  for (const name of e.unit.names) {
    const alias = name === e.spelling ? null : name;
    const nameLower = name.toLowerCase();
    if (name === term) consider(100, alias);
    else if (nameLower === lower) consider(90, alias);
    else if (nameLower.startsWith(lower)) consider(70 - (name.length - term.length) / 10, alias);
    else if (lower.length >= 2 && nameLower.includes(lower)) consider(30, alias);
  }
  for (const text of [e.label, ...e.keywords, e.category]) {
    const textLower = text.toLowerCase();
    if (textLower.split(/\s+/).some((word) => word.startsWith(lower))) consider(text === e.category ? 40 : 60, null);
    else if (textLower.startsWith(lower)) consider(55, null);
  }
  if (best.score === 0 && lower.length >= 3) {
    const allowed = lower.length <= 4 ? 1 : 2;
    for (const name of e.unit.names) {
      if (name.length >= 2 && damerauLevenshteinDistance(lower, name.toLowerCase()).distance <= allowed) {
        consider(20, name === e.spelling ? null : name);
      }
    }
  }

  // Spellings, names, and keywords are confident; categories, substrings, and typos are not:
  const confident = best.score >= 55;
  return { score: best.score > 0 ? best.score + (e.common ? 5 : 0) : 0, matchedAlias: best.matchedAlias, confident };
}

function unitOptionFor(
  spelling: string,
  matchedAlias: string | null,
  continueWith: UnitOption['continueWith'],
  confident: boolean,
) {
  const known = catalogByName.get(spelling);
  return {
    spelling,
    description: unitStringDescribe(spelling),
    dimension: unitDimensionDescribe(UnitsTracker.fromParsedUnitString(spelling)),
    note: known && known.spelling === spelling ? known.note : null,
    matchedAlias,
    continueWith,
    confident,
  } satisfies UnitOption;
}

/**
 * Whether `partial` could become `target` by appending more units, e.g. `lbs` and `lbs/MMBtu` can both become
 * `lbs/MMBtu-hr`. Returns what to type next, or null if it can't.
 */
function unitContinuation(partial: UnitsTracker, target: UnitsTracker, partialHasSlash: boolean): '/' | '-' | null {
  const have = partial.dimensionExponents();
  const want = target.dimensionExponents();
  let missingNumerator = false;
  let missingDenominator = false;
  for (const [name, wanted] of want) {
    const had = have.get(name) ?? 0;
    if (had !== 0 && Math.sign(had) !== Math.sign(wanted)) return null;
    if (Math.abs(had) > Math.abs(wanted)) return null;
    if (Math.abs(had) < Math.abs(wanted)) {
      if (wanted > 0) missingNumerator = true;
      else missingDenominator = true;
    }
  }
  for (const name of have.keys()) {
    if (!want.has(name)) return null;
  }

  if (missingDenominator) {
    return partialHasSlash ? '-' : '/';
  } else if (missingNumerator && !partialHasSlash) {
    return '-';
  }
  return null;
}

/**
 * Compound units made of common units that convert to the target, e.g. for `lbs/hr`: `tons/yr`, `kg/hr`, …. The target
 * itself always comes first.
 */
function unitCompoundsConvertibleTo(target: string): string[] {
  const parsed = unitStringParse(target);
  if ('error' in parsed || parsed.parts.some((p) => !catalogByName.has(p.name))) {
    return [];
  }

  const signature = (spelling: string) =>
    JSON.stringify([...UnitsTracker.fromParsedUnitString(spelling).dimensionExponents()]);
  const choicesPerPart = parsed.parts.map((part) => {
    const own = catalogByName.get(part.name)!;
    const ownSignature = signature(own.spelling);
    const same = unitCatalog.filter((e) => e !== own && e.common && signature(e.spelling) === ownSignature);
    return [part.name, ...same.map((e) => e.spelling)].map((name) => ({ name, exponent: part.exponent }));
  });

  let combinations: UnitPart[][] = [[]];
  for (const choices of choicesPerPart) {
    combinations = combinations.flatMap((combo) => choices.map((choice) => [...combo, choice])).slice(0, 60);
  }

  return [...new Set(combinations.map(unitPartsToString))].filter((spelling) =>
    UnitsTracker.canConvert({ from: spelling, to: target }),
  );
}

/** How a whole unit string matches a query: by spelling or a word of its description is confident, otherwise weak. */
function unitStringMatch(spelling: string, query: string): 'confident' | 'weak' | null {
  const lower = query.toLowerCase();
  const description = unitStringDescribe(spelling).toLowerCase();
  if (spelling.toLowerCase().startsWith(lower) || description.split(/[\s-]+/).some((w) => w.startsWith(lower))) {
    return 'confident';
  }
  return description.includes(lower) ? 'weak' : null;
}

function unitStringOptions(spellings: readonly string[], query: string): UnitOption[] {
  return spellings.flatMap((spelling) => {
    if (query === '') return [unitOptionFor(spelling, null, null, false)];
    const match = unitStringMatch(spelling, query);
    return match ? [unitOptionFor(spelling, null, null, match === 'confident')] : [];
  });
}

const maxOptionsPerGroup = 30;

/** Whether a picker may choose a unit. Custom units are only analyzed as such when the restriction allows them. */
function unitStringAccepted(spelling: string, restriction: UnitRestriction): boolean {
  const { status } = unitStringAnalyze(spelling, restriction);
  return status === 'ok' || status === 'custom';
}

/**
 * The option for completing `head` (the units typed so far) with a catalog entry that matches `segment`, or null if the
 * entry doesn't match, or can't lead to a unit the restriction accepts.
 */
function unitCompletion(
  e: UnitCatalogEntry,
  head: string,
  segment: string,
  restriction: UnitRestriction,
): { option: UnitOption; score: number } | null {
  const { score, matchedAlias, confident } = unitEntryScore(e, segment);
  if (score === 0) {
    return null;
  }

  const spelling = head + e.spelling;
  if ('error' in unitStringParse(spelling)) {
    return null;
  } else if (unitStringAccepted(spelling, restriction)) {
    return { option: unitOptionFor(spelling, matchedAlias, null, confident), score: score + 1000 };
  } else if (restriction.kind === 'convertibleTo') {
    const continueWith = unitContinuation(
      UnitsTracker.fromParsedUnitString(spelling),
      UnitsTracker.fromParsedUnitString(restriction.unit),
      spelling.includes('/'),
    );
    return continueWith ? { option: unitOptionFor(spelling, matchedAlias, continueWith, confident), score } : null;
  }
  return null;
}

/** Compound units worth suggesting, e.g. lbs/hr, either for any purpose or because they convert to what's required. */
function unitCompoundCandidates(restriction: UnitRestriction): string[] {
  const candidates =
    restriction.kind === 'convertibleTo' ? unitCompoundsConvertibleTo(restriction.unit) : unitCommonCompounds;
  return candidates.filter((spelling) => unitStringAccepted(spelling, restriction));
}

/** Whether a restriction requires a compound unit, like "mass per time", rather than a simple one, like "mass". */
function unitRestrictionIsCompound(restriction: UnitRestriction): boolean {
  return restriction.kind === 'convertibleTo' && UnitsTracker.fromParsedUnitString(restriction.unit).exponents.size > 1;
}

/**
 * Options for a unit picker, given what has been typed so far. Typing `lbs/h` completes the last piece (`lbs/hr`), so
 * compound units can be built one unit at a time.
 */
export function unitOptionsSearch(
  query: string,
  restriction: UnitRestriction,
  recommended: readonly string[],
): UnitOptionGroup[] {
  const trimmed = query.trim();
  const groups: UnitOptionGroup[] = [];
  const seen = new Set<string>();
  const push = (heading: string, options: UnitOption[]) => {
    const fresh = options.filter((o) => !seen.has(o.spelling)).slice(0, maxOptionsPerGroup);
    fresh.forEach((o) => seen.add(o.spelling));
    if (fresh.length > 0) groups.push({ heading, options: fresh });
  };
  push('Recommended', unitStringOptions(recommended, trimmed));

  if (restriction.kind === 'oneOf') {
    push('Units', unitStringOptions(restriction.units, trimmed));
    return groups;
  }

  // A whole shorthand like tpy:
  const shorthand = unitShorthands.get(trimmed.toLowerCase());
  if (shorthand && unitStringAccepted(shorthand, restriction)) {
    push('Matches', [unitOptionFor(shorthand, trimmed, null, true)]);
  }

  // Units never contain spaces, so a query with spaces is a description to search for, e.g. "natural gas":
  const { head, segment } = /\s/.test(trimmed) ? { head: '', segment: trimmed } : unitQuerySplit(trimmed);
  const headParsed = head === '' ? null : unitStringParse(head.replace(/[-/]$/, ''));
  if (headParsed && 'error' in headParsed) {
    return groups;
  }

  const completions = unitCatalog
    .filter((e) => e.category !== 'Calendar')
    .flatMap((e) => unitCompletion(e, head, segment, restriction) ?? [])
    .sort((a, b) => b.score - a.score);
  const compounds = unitStringOptions(unitCompoundCandidates(restriction), trimmed);

  // Before anything is typed, compound units are the more useful suggestions when a compound unit is required:
  if (trimmed === '' && unitRestrictionIsCompound(restriction)) {
    push('Suggested', compounds);
    push(
      'Build your own',
      completions.map((c) => c.option),
    );
  } else {
    push(
      trimmed === '' ? 'Units' : 'Matches',
      completions.map((c) => c.option),
    );
    push(trimmed === '' ? 'Common combinations' : 'Combinations', compounds);
  }

  return groups;
}

/**
 * Options for browsing before anything has been typed, for people who would rather look through the units than guess
 * at what to search for. Every unit is sorted into its category, and categories with nothing acceptable are left out.
 */
export function unitOptionsBrowse(restriction: UnitRestriction, recommended: readonly string[]): UnitOptionGroup[] {
  if (restriction.kind === 'oneOf') {
    return unitOptionsSearch('', restriction, recommended);
  }

  const groups: UnitOptionGroup[] = [];
  const push = (heading: string, options: UnitOption[]) => {
    if (options.length > 0) groups.push({ heading, options });
  };

  push('Recommended', unitStringOptions(recommended, ''));
  // When a simple unit is required, the units convertible to it are already in its category:
  if (restriction.kind === 'any' || unitRestrictionIsCompound(restriction)) {
    push(
      restriction.kind === 'any' ? 'Common combinations' : 'Suggested',
      unitStringOptions(unitCompoundCandidates(restriction), ''),
    );
  }

  const categories = [...new Set(unitCatalog.map((e) => e.category))].filter((c) => c !== 'Calendar');
  for (const category of categories) {
    push(
      category,
      unitCatalog
        .filter((e) => e.category === category)
        .flatMap((e) => unitCompletion(e, '', '', restriction)?.option ?? []),
    );
  }

  return groups;
}

export interface UnitSampleConversion {
  /** How much of the unit is converted, chosen so that no conversion is a hard-to-read sliver, e.g. 100 lbs. */
  amount: number;
  conversions: { unit: string; amount: number }[];
}

const maxSampleConversions = 2;

/**
 * A worked example of a unit, e.g. `1 lbs/hr = 4.38 tons/yr = 24 lbs/day`. Someone who doesn't know what a spelling
 * means (tons or tonnes? oz or floz?) can still see whether the numbers are what they expect. The units the context
 * cares about come first: what's recommended, then what the restriction requires, then common units of the same kind.
 * Null when the unit can't be converted, or there is nothing different to convert it to.
 */
export function unitSampleConversions(
  value: string,
  restriction: UnitRestriction,
  recommended: readonly string[],
): UnitSampleConversion | null {
  const trimmed = value.trim();
  if (unitStringAnalyze(trimmed, restriction).status !== 'ok') {
    return null;
  }

  const from = UnitsTracker.fromParsedUnitString(trimmed);
  const candidates = [
    ...recommended,
    ...(restriction.kind === 'convertibleTo' ? [restriction.unit] : []),
    ...unitCommonCompounds,
    ...unitCatalog.filter((e) => e.common).map((e) => e.spelling),
    // Uncommon compound units, like lbs/MMBtu-hr, still have common relatives:
    ...unitCompoundsConvertibleTo(trimmed),
  ];

  const conversions: { unit: string; tracker: UnitsTracker; factor: number }[] = [];
  for (const unit of candidates) {
    if (conversions.length >= maxSampleConversions) break;
    const tracker = UnitsTracker.fromParsedUnitString(unit);
    const factor = from.convertTo(tracker, 1);
    // Skip other spellings of the same unit (pounds and lbs), and units equal to it (cuft and ft^3):
    if (typeof factor !== 'number' || factor === 1 || from.equals(tracker)) continue;
    if (conversions.some((c) => c.tracker.equals(tracker))) continue;
    conversions.push({ unit, tracker, factor });
  }
  if (conversions.length === 0) {
    return null;
  }

  // 1 lbs = 0.0005 tons is hard to read, but 100 lbs = 0.05 tons is not:
  const smallest = Math.min(...conversions.map((c) => Math.abs(c.factor)));
  let amount = 1;
  while (smallest * amount < 0.01 && amount < 1_000_000) {
    amount *= 10;
  }

  return { amount, conversions: conversions.map(({ unit, factor }) => ({ unit, amount: factor * amount })) };
}
